import { IconHeart, IconHeartFilled } from "@tine/designsystem-icons/sharp";
import { Checkbox } from "@tine/designsystem-ui-react";
import size from "lodash-es/size";
import sortBy from "lodash-es/sortBy";
import toLower from "lodash-es/toLower";
import values from "lodash-es/values";
import { observer } from "mobx-react-lite";
import { forwardRef, useRef, useState } from "react";
import { sendAddItemsToFavoriteList, sendRemoveItemFromFavoriteList } from "../common/tracking";
import type { GAListInformation } from "../common/types/trackingTypes";
import { isErrorOccured } from "../common/utils/asyncDataUtils";
import authStore from "../stores/auth/authStore";
import favoriteStore from "../stores/favoriteStore";
import productStore from "../stores/product/productStore";
import Button from "./Button";
import { Popover, PopoverContent, PopoverTrigger } from "./common/popover/Popover";
import ErrorAlertWithOptionalTracking from "./ErrorAlertWithOptionalTracking";

type FavoriteProps = {
   sku: string;
   listInformation: GAListInformation;
   position: number;
};

type FavoriteHeartIconProps = {
   isFavorite: boolean;
   onClick?: () => void;
   disabled?: boolean;
};

const FavoriteHeartIcon = forwardRef<HTMLDivElement, FavoriteHeartIconProps>(({ isFavorite, onClick, disabled = false }, ref) => (
   <>
      {!disabled && (
         <button type="button" onClick={onClick} data-testid="favorite-icon-button">
            <div ref={ref}>
               {isFavorite ? <IconHeartFilled className="fill-favorite" /> : <IconHeart className="hover:fill-favorite" />}
            </div>
         </button>
      )}
      {disabled && (
         <div className="cursor-not-allowed">
            <IconHeart className="fill-base-300" />
         </div>
      )}
   </>
));

const Favorite = ({ sku, listInformation, position }: FavoriteProps) => {
   const [show, setShow] = useState<boolean>(false);
   const isFavorite = favoriteStore.isFavorite(sku);
   const isLoggedIn = authStore.isLoggedIn();
   const hasMultipleLists = size(favoriteStore.allFavoriteLists.data) > 1;
   const parentRef = useRef<HTMLDivElement>(null);

   const errorTracking = {
      context: "favorite_lists",
      text: "add_favorite_error"
   };

   if (!isLoggedIn) {
      return <FavoriteHeartIcon disabled isFavorite={isFavorite} />;
   }

   const addFavorite = (sku: string, listId?: string) => {
      const product = productStore.resolveSku(sku, "inAssortment");
      if (product) {
         favoriteStore.addFavorite(sku, listId);
         sendAddItemsToFavoriteList([product], listInformation, position);
      }
   };

   const removeFavorite = (sku: string, listId?: string) => {
      favoriteStore.removeFavorite(sku, listId);
      sendRemoveItemFromFavoriteList(sku, listInformation, position);
   };

   const handleClick = () => {
      if (isLoggedIn) {
         isFavorite ? removeFavorite(sku) : addFavorite(sku);
      }
   };

   if (hasMultipleLists) {
      const lists = sortBy(values(favoriteStore.allFavoriteLists.data), (l) => toLower(l.listName));
      const favoriteListPicker = (
         <div>
            <h3 className="px-4 py-2 bg-base-100">Velg liste</h3>
            <div className="p-4">
               <ul className="list-none ps-0">
                  {lists.map((list) => {
                     const checked = typeof list.items[sku] === "number" && list.items[sku] > 0;
                     return (
                        <li key={list.id} translate="no">
                           <Checkbox
                              className="mb-2 min-h-6 text-start"
                              label={list.listName}
                              isChecked={checked}
                              onChange={() => (checked ? removeFavorite(sku, list.id) : addFavorite(sku, list.id))}
                           />
                        </li>
                     );
                  })}
               </ul>
               {isErrorOccured(favoriteStore.saveFavoritesProgress) && (
                  <ErrorAlertWithOptionalTracking
                     title="Feil under lagring"
                     showCloseButton={false}
                     gaErrorTracking={errorTracking}
                  >
                     Vi greide dessverre ikke å lagre endringen i favorittlister. Prøv igjen senere.
                  </ErrorAlertWithOptionalTracking>
               )}
               <div className="flex justify-center mt-2">
                  <Button
                     data-testid="add-to-favorite-list-popover-button"
                     onClick={() => {
                        setShow(false);
                        favoriteStore.clearSaveFavoritesProgress();
                     }}
                  >
                     OK
                  </Button>
               </div>
            </div>
         </div>
      );

      return (
         <Popover
            open={show}
            onOpenChange={(open) => {
               setShow(open);
               if (!open) {
                  favoriteStore.clearSaveFavoritesProgress();
               }
            }}
         >
            <PopoverTrigger asChild>
               <div className="flex justify-center mb-1">
                  <FavoriteHeartIcon onClick={() => setShow(!show)} isFavorite={isFavorite} ref={parentRef} />
               </div>
            </PopoverTrigger>
            <PopoverContent>{favoriteListPicker}</PopoverContent>
         </Popover>
      );
   }

   return <FavoriteHeartIcon isFavorite={isFavorite} onClick={handleClick} />;
};

export default observer(Favorite);
